import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { subscriptionsAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { KpiCard, PillTabs, SectionCard, MonthlyStackedChart, fmtMoney } from './tax/TaxDashboardParts';
import {
  TYPE_SERIES, flattenRows, typeMonthChart, entityMonthChart, entityTable, typeStats, monthLabel,
} from '../lib/fixedCosts';
import { SubscriptionsVendorListTab } from './SubscriptionsVendorListTab';
import { SubscriptionsOutgoingsTab } from './SubscriptionsOutgoingsTab';

// Fixed costs page body, shared by Entities and Personal. Fan, 2026-10-29:
// subscriptions one tab, the rest another, an overview with both; then "make
// the overview, subscriptions and other fixed costs more like the taxes tab"
// with month bars split subscriptions vs others. Same layout parts as Taxes
// (KPI cards, pill tabs, section cards, stacked month chart). Monthly only:
// "take out the cost per year".

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'subscription', label: 'Subscriptions' },
  { key: 'rent', label: 'Other fixed costs' },
  { key: 'all', label: 'All payees' },
];

function EntityTable({ rows, cur, scope, showTypes, testId }) {
  const [open, setOpen] = useState({});
  const money = (v) => fmtMoney(v, cur);
  const total = rows.reduce((s, e) => s + e.total, 0);
  const who = scope === 'personal' ? 'Person' : 'Company';
  return (
    <table className="w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="border-b text-muted-foreground text-xs">
          <th className="text-left py-2 pr-3">{who}</th>
          {showTypes && <th className="text-right py-2 pr-3">Subscriptions</th>}
          {showTypes && <th className="text-right py-2 pr-3">Other fixed costs</th>}
          <th className="text-right py-2 pr-3">Per month</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <React.Fragment key={e.key}>
            <tr className="border-b border-border/50 hover:bg-muted/40">
              <td className="py-2 pr-3">
                <button
                  type="button"
                  className="flex items-center gap-1 font-medium text-left"
                  onClick={() => setOpen((o) => ({ ...o, [e.key]: !o[e.key] }))}
                  data-testid={`${testId}-toggle-${e.key}`}
                >
                  {open[e.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {e.name}
                  <span className="text-xs text-muted-foreground font-normal ml-1">({e.vendors.length})</span>
                </button>
              </td>
              {showTypes && <td className="py-2 pr-3 text-right font-mono">{money(e.subscription)}</td>}
              {showTypes && <td className="py-2 pr-3 text-right font-mono">{money(e.rent)}</td>}
              <td className="py-2 pr-3 text-right font-mono font-semibold">{money(e.total)}</td>
            </tr>
            {open[e.key] && e.vendors.map((v) => (
              <tr key={`${e.key}-${v.vendor}-${v.currency}-${v.spend_type}`} className="border-b border-border/30 text-xs">
                <td className="py-1 pr-3 pl-7 text-muted-foreground">
                  {v.vendor}
                  {v.basis === 'cost_entry_no_bank_payment' && (
                    <span className="ml-1 italic" title="From the Cost Entry register; no matching payment found in the bank feeds in the last 3 months">(from Cost Entry, not seen in bank)</span>
                  )}
                  {v.currency !== cur && !v.unconvertible && (
                    <span className="ml-1">({fmtMoney(v.monthly_native, v.currency)})</span>
                  )}
                </td>
                {showTypes && (
                  <td className="py-1 pr-3 text-right font-mono text-muted-foreground">
                    {v.spend_type === 'subscription' ? money(v.monthly) : ''}
                  </td>
                )}
                {showTypes && (
                  <td className="py-1 pr-3 text-right font-mono text-muted-foreground">
                    {v.spend_type === 'rent' ? money(v.monthly) : ''}
                  </td>
                )}
                <td className="py-1 pr-3 text-right font-mono text-muted-foreground">
                  {v.unconvertible ? fmtMoney(v.monthly_native, v.currency) : money(v.monthly)}
                </td>
              </tr>
            ))}
          </React.Fragment>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={showTypes ? 4 : 2} className="py-6 text-center text-muted-foreground">Nothing here.</td></tr>
        )}
      </tbody>
      {rows.length > 0 && (
        <tfoot>
          <tr className="text-sm font-semibold">
            <td className="py-2 pr-3">Total</td>
            {showTypes && <td className="py-2 pr-3 text-right font-mono">{money(rows.reduce((s, e) => s + e.subscription, 0))}</td>}
            {showTypes && <td className="py-2 pr-3 text-right font-mono">{money(rows.reduce((s, e) => s + e.rent, 0))}</td>}
            <td className="py-2 pr-3 text-right font-mono">{money(total)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

function NotCounted({ summary, cur }) {
  const ex = summary?.excluded;
  if (!ex || !ex.count) return null;
  return (
    <div className="text-xs text-muted-foreground" data-testid="fixed-not-counted">
      <p>Not counted: {fmtMoney(ex.monthly, cur)}/mo of payments that are not a recurring cost (see All payees, "Not counted"):</p>
      <ul className="mt-1 space-y-0.5">
        {(ex.by_reason || []).map((r) => (
          <li key={r.reason}>{fmtMoney(r.monthly, cur)}/mo, {r.label} ({r.count})</li>
        ))}
      </ul>
    </div>
  );
}

function Overview({ summary, rows, cur, scope }) {
  const byType = {};
  (summary?.by_spend_type || []).forEach((b) => { byType[b.spend_type] = b; });
  const subs = byType.subscription || {};
  const rent = byType.rent || {};
  const review = byType.unclassified || {};
  const cancellable = summary?.cancellable || {};
  const chart = useMemo(() => typeMonthChart(rows), [rows]);
  const table = useMemo(() => entityTable(rows), [rows]);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fixed-kpis">
        <KpiCard testId="fixed-kpi-total" label="Total fixed costs" value={`${fmtMoney(summary?.committed?.monthly, cur)}/mo`}
          subDetail="Subscriptions plus other fixed costs, average of the last 3 full months" />
        <KpiCard testId="fixed-kpi-subscriptions" label={`Subscriptions (${subs.count || 0})`} value={`${fmtMoney(subs.monthly, cur)}/mo`}
          subDetail={cancellable.count ? `${cancellable.count} could be cancelled (${fmtMoney(cancellable.monthly, cur)}/mo)` : 'Software, tools and services'} />
        <KpiCard testId="fixed-kpi-other" label={`Other fixed costs (${rent.count || 0})`} value={`${fmtMoney(rent.monthly, cur)}/mo`}
          subDetail="Rent, council tax, copro, utilities, insurance, loans" />
        <KpiCard testId="fixed-kpi-review" label={`Needs review (${review.count || 0})`} value={`${fmtMoney(review.monthly, cur)}/mo`}
          tone={review.count ? 'bad' : undefined} subDetail="Recurring, not classified yet. Not in the total." />
      </div>
      <SectionCard title={`Fixed costs by month (${cur})`} subtitle="What was actually paid each month, subscriptions vs other fixed costs." testId="fixed-chart-type">
        {chart.length
          ? <MonthlyStackedChart data={chart} series={TYPE_SERIES} ccy={cur} height={260} />
          : <p className="text-sm text-muted-foreground py-6 text-center">No payments in the last 3 months.</p>}
      </SectionCard>
      <SectionCard title={scope === 'personal' ? 'By person' : 'By entity'} subtitle="Monthly average. Click a name to see each payee." testId="fixed-by-entity">
        <EntityTable rows={table} cur={cur} scope={scope} showTypes testId="fixed-entity-table" />
      </SectionCard>
      <NotCounted summary={summary} cur={cur} />
    </>
  );
}

function TypeTab({ spendType, rows, cur, scope }) {
  const label = spendType === 'subscription' ? 'Subscriptions' : 'Other fixed costs';
  const stats = useMemo(() => typeStats(rows, spendType), [rows, spendType]);
  const chart = useMemo(() => entityMonthChart(rows, spendType), [rows, spendType]);
  const table = useMemo(() => entityTable(rows, [spendType]), [rows, spendType]);
  const who = scope === 'personal' ? 'person' : 'company';
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`fixed-kpis-${spendType}`}>
        <KpiCard testId={`fixed-kpi-${spendType}-total`} label={label} value={`${fmtMoney(stats.total, cur)}/mo`}
          subDetail="Average of the last 3 full months" />
        <KpiCard label="Payees" value={String(stats.count)} subDetail={`Across ${table.length} ${who === 'person' ? 'people' : 'companies'}`} />
        <KpiCard label="Biggest" value={stats.biggest ? `${fmtMoney(stats.biggest.monthly, cur)}/mo` : '-'}
          subDetail={stats.biggest ? `${stats.biggest.vendor}, ${stats.biggest.entity_name}` : null} />
        <KpiCard label={stats.lastMonthKey ? `Paid in ${monthLabel(stats.lastMonthKey)}` : 'Last month'}
          value={stats.lastMonth == null ? '-' : fmtMoney(stats.lastMonth, cur)} subDetail="Latest full month" />
      </div>
      <SectionCard title={`${label} by month (${cur})`} subtitle={`What was actually paid each month, by ${who}.`} testId={`fixed-chart-${spendType}`}>
        {chart.data.length
          ? <MonthlyStackedChart data={chart.data} series={chart.series} ccy={cur} height={260} />
          : <p className="text-sm text-muted-foreground py-6 text-center">No payments in the last 3 months.</p>}
      </SectionCard>
      <SectionCard title={scope === 'personal' ? 'By person' : 'By entity'} subtitle="Monthly average. Click a name to see each payee." testId={`fixed-by-entity-${spendType}`}>
        <EntityTable rows={table} cur={cur} scope={scope} testId={`fixed-entity-table-${spendType}`} />
      </SectionCard>
      <SectionCard title="All payees" subtitle="Searchable, with payments and last payment date.">
        <SubscriptionsVendorListTab scope={scope} fixedSpendType={spendType} />
      </SectionCard>
    </>
  );
}

export function FixedCostsTabs({ scope = 'entities' }) {
  const [tab, setTab] = useState('overview');
  const selectedEntity = useEntityStore((s) => s.selectedEntity);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [summary, setSummary] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entityParam = selectedEntity && selectedEntity !== 'all' && !String(selectedEntity).startsWith('group:')
    ? selectedEntity
    : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { entityId: entityParam, scope: scope === 'personal' ? 'personal' : 'entities', displayCurrency };
    try {
      const [s, t] = await Promise.all([
        subscriptionsAPI.summary(params),
        subscriptionsAPI.byJurisdiction(params),
      ]);
      setSummary(s.data);
      setTree(t.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [entityParam, scope, displayCurrency]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => flattenRows(tree), [tree]);
  const cur = summary?.display_currency || tree?.display_currency || displayCurrency;

  return (
    <div className="flex flex-col gap-6" data-testid="fixed-costs">
      <PillTabs value={tab} onChange={setTab} options={TABS} testId="fixed-tabs" />
      {tab !== 'all' && loading && (
        <Card><CardContent className="py-12 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>
      )}
      {tab !== 'all' && !loading && error && <p className="text-sm text-destructive">{error}</p>}
      {tab === 'overview' && !loading && !error && <Overview summary={summary} rows={rows} cur={cur} scope={scope} />}
      {(tab === 'subscription' || tab === 'rent') && !loading && !error && (
        <TypeTab key={tab} spendType={tab} rows={rows} cur={cur} scope={scope} />
      )}
      {tab === 'all' && (
        <>
          <SubscriptionsVendorListTab scope={scope} />
          <details className="rounded-md border p-3" data-testid="fixed-manual-outgoings">
            <summary className="cursor-pointer text-sm text-muted-foreground">Monthly outgoings set by hand</summary>
            <div className="mt-4">
              <SubscriptionsOutgoingsTab scope={scope} />
            </div>
          </details>
        </>
      )}
    </div>
  );
}
